import React, { type ReactNode, useCallback, useEffect, useRef } from 'react';

import useOnClickOutside from '../../react-hooks/use-outside-click';

export interface ToastContainerProps {
  /**
   * The duration in milliseconds to wait before automatically dismissing the toast.
   * When set to -1, the toast will not automatically dismiss, i.e. auto-dismiss is disabled.
   * @default 6000
   */
  autoDismissDelay?: number;
  children: ReactNode;
  /**
   * Style class name to apply to the toast container. Useful for applying
   * container styling and animation.
   */
  className: string;
  /**
   * When true, the toast will not dimiss when the user clicks outside of it.
   * Useful in scenarios where the user may need to click around while the
   * toast is still visible.
   *
   * @default false
   */
  disableOutsideClickDismiss?: boolean;
  /**
   * A callback function that is invoked when an attached CSS animation ends.
   */
  onAnimationEnd?: () => void;
  /**
   * This event is fired when the toast is indirectly dismissed, such as when
   * the auto-dismiss timer expires, or when the user clicks outside of the toast,
   * or when the user presses the escape key.
   */
  onIndirectDismiss: () => void;
}

const ToastContainer = ({
  autoDismissDelay = 6000,
  children,
  className,
  disableOutsideClickDismiss = false,
  onAnimationEnd,
  onIndirectDismiss,
}: ToastContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMouseInContainer = useRef<boolean>(false);
  const timerId = useRef<number | undefined>();

  const setAutoDismissDelayTimer = useCallback(
    (delay: number) => {
      timerId.current = window.setTimeout(() => {
        if (!isMouseInContainer.current) {
          onIndirectDismiss?.();
        }
      }, delay);
    },
    [onIndirectDismiss]
  );

  // Setup timer for toast duration .
  useEffect(() => {
    if (autoDismissDelay > -1) {
      setAutoDismissDelayTimer(autoDismissDelay);
    }

    return () => {
      if (timerId.current) {
        window.clearTimeout(timerId.current);
      }
    };
  }, [autoDismissDelay, setAutoDismissDelayTimer]);

  // Setup mouse enter handler to prevent the toast from auto-closing
  // when the mouse is over it and duration expires.
  const handleMouseEnter = useCallback(() => {
    isMouseInContainer.current = true;
  }, []);

  // Setup mouse leave handler to set the duration timer when the
  // mouse leaves the toast
  const handleMouseLeave = useCallback(() => {
    isMouseInContainer.current = false;
    if (autoDismissDelay > -1) {
      setAutoDismissDelayTimer(autoDismissDelay);
    }
  }, [autoDismissDelay, setAutoDismissDelayTimer]);

  // Setup handler for "click outside" events, respecting the
  // `preventOutsideClickDismissBeforeAutoDismiss` value when necessary.
  const handleClickOutside = useCallback(() => {
    if (!disableOutsideClickDismiss) {
      onIndirectDismiss();
    }
  }, [onIndirectDismiss, disableOutsideClickDismiss]);

  // Setup "click outside" handling
  useOnClickOutside({ elemRef: containerRef, onClickOutside: handleClickOutside });

  // Setup escape key handling
  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        handleClickOutside();
      }
    };

    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleClickOutside]);

  return (
    <div
      className={className}
      onAnimationEnd={onAnimationEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
    >
      {children}
    </div>
  );
};

export default ToastContainer;
